import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Play, Radio, TrendingUp, Users } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { usePlayer } from "@/context/PlayerContext";

/**
 * TrendingStation — the full trending list on the homepage. Replaces the
 * old top-5 with an "everybody plays" stacked queue: the backend already
 * fair-orders the list so each artist's best track appears FIRST (one per
 * artist), then the rest of the catalog is appended by play count.
 *
 * The list scrolls vertically inside its own frame so the page doesn't
 * stretch on catalogs with 100+ tracks, but the whole catalog is playable.
 */
export default function TrendingStation() {
  const [tracks, setTracks] = useState([]);
  const { playRadio, current, playing, mode } = usePlayer();

  useEffect(() => {
    api
      .get("/radio/top?limit=200")
      .then((r) => setTracks(r.data.tracks || []))
      .catch(() => setTracks([]));
  }, []);

  if (tracks.length === 0) return null;

  const artistCount = new Set(tracks.map((t) => t.artist_id)).size;
  const maxPlays = Math.max(...tracks.map((t) => t.play_count || t.plays || 0), 1);

  return (
    <section
      data-testid="trending-station"
      className="relative border-b border-[#2A2A2A] bg-[#0A0A0A] overflow-hidden"
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(50% 60% at 100% 50%, rgba(255,69,0,0.08), transparent 70%)" }}
      />
      <div className="relative max-w-[1400px] mx-auto px-4 md:px-8 py-14 md:py-20">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF4500] mb-2 flex items-center gap-2">
              <TrendingUp className="h-4 w-4" /> 01 / The trending station
            </div>
            <h2 className="font-display font-black text-4xl md:text-6xl tracking-tighter leading-[0.9]">
              Every artist.<br />
              <span className="text-[#FF4500]">One free channel.</span>
            </h2>
            <p className="font-mono text-xs text-gray-400 mt-4 max-w-md leading-relaxed">
              Fair rotation — every artist's best track lands on the queue first,
              then the rest stacks by play count. Tap any row to jump in.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-[10px] uppercase tracking-widest text-gray-500">
              <span className="flex items-center gap-1.5">
                <Users className="h-3 w-3 text-[#FF4500]" /> {artistCount} artist{artistCount === 1 ? "" : "s"}
              </span>
              <span>· {tracks.length} track{tracks.length === 1 ? "" : "s"} in rotation</span>
            </div>
          </div>
          {tracks[0] && (
            <button
              data-testid="trending-tune-in"
              onClick={() => playRadio(tracks[0], tracks)}
              className="group flex items-center gap-2 bg-[#FF4500] text-black px-5 py-3 font-mono text-xs uppercase tracking-widest hover:bg-[#E53E00] transition-colors shadow-[0_10px_40px_-10px_rgba(255,69,0,0.9)]"
            >
              <Radio className="h-4 w-4" />
              Tune in
              <Play className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" fill="currentColor" />
            </button>
          )}
        </div>

        {/* Scrollable list — capped height so a 100-track catalog doesn't push the page 10 screens down */}
        <div
          data-testid="trending-list"
          className="border border-[#2A2A2A] bg-[#0d0d0d] max-h-[560px] overflow-y-auto divide-y divide-[#2A2A2A]"
        >
          {tracks.map((t, i) => {
            const plays = t.play_count || t.plays || 0;
            const pct = Math.max(6, (plays / maxPlays) * 100);
            const isNowPlaying = current?.id === t.id && mode === "radio";
            return (
              <motion.button
                key={t.id}
                data-testid={`trending-row-${i}`}
                onClick={() => playRadio(t, tracks)}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: Math.min(i * 0.02, 0.4), duration: 0.35 }}
                className={`group relative w-full text-left transition-colors overflow-hidden ${
                  isNowPlaying ? "bg-[#FF4500]/10" : "hover:bg-[#131313]"
                }`}
              >
                {/* Play-count fill bar */}
                <motion.div
                  className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#FF4500]/15 to-transparent"
                  initial={{ width: 0 }}
                  whileInView={{ width: `${pct}%` }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.15, duration: 0.9, ease: "easeOut" }}
                />
                <div className="relative flex items-center gap-3 md:gap-5 p-3 md:p-4">
                  {/* Rank */}
                  <div className="font-display font-black text-2xl md:text-3xl leading-none tracking-tighter text-[#FF4500] w-10 md:w-14 shrink-0 tabular-nums text-right">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  {/* Cover thumb */}
                  {t.cover_path ? (
                    <img
                      src={fileUrl(t.cover_path)}
                      alt=""
                      loading="lazy"
                      className="h-10 w-10 md:h-12 md:w-12 object-cover border border-white/10 shrink-0"
                    />
                  ) : (
                    <div className="h-10 w-10 md:h-12 md:w-12 bg-[#FF4500]/20 grid place-items-center shrink-0">
                      <Radio className="h-4 w-4 text-[#FF4500]" />
                    </div>
                  )}
                  {/* Title + Artist */}
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-bold text-sm md:text-base truncate">
                      {t.title}
                      {isNowPlaying && playing && (
                        <span className="ml-2 font-mono text-[9px] uppercase tracking-widest text-[#FF4500] align-middle">
                          On air
                        </span>
                      )}
                    </div>
                    <div className="font-mono text-[10px] md:text-xs text-gray-400 truncate">
                      {t.artist_name}
                    </div>
                  </div>
                  {/* Play count */}
                  <div className="hidden sm:block text-right shrink-0">
                    <div className="font-display text-lg tabular-nums">{compact(plays)}</div>
                    <div className="font-mono text-[9px] uppercase tracking-widest text-gray-500">plays</div>
                  </div>
                  {/* Play button */}
                  <div className="h-8 w-8 md:h-10 md:w-10 grid place-items-center border border-[#FF4500]/40 group-hover:bg-[#FF4500] group-hover:border-[#FF4500] transition-colors shrink-0">
                    <Play
                      className="h-3.5 w-3.5 text-[#FF4500] group-hover:text-black transition-colors translate-x-[1px]"
                      fill="currentColor"
                    />
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function compact(n) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}
